import { useState } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import {
  Bars3Icon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { Link } from "~/components/ui/link";
import logo from "./mana-logo.avif";
import { useLocation } from "react-router";
import { classNames } from "~/utils";

type NavigationItem = {
  name: string;
  href: string;
  target?: string;
  onClick?: () => void;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

export default function Menu() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isCurrent = (path: string) => location.pathname === path;

  let navigation: NavigationItem[] = [
    { name: "Chat", href: "/chat/new" },
    { name: "About", href: "/about" },
    { name: "Integration", href: "/integration" },
    {
      name: "Company",
      href: "https://www.mana.bio/",
      target: "_blank",
      icon: ArrowTopRightOnSquareIcon,
    },
  ];

  return (
    <div>
      <header className="bg-white">
        <nav
          aria-label="Global"
          className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8"
        >
          <div className="flex lg:flex-1">
            <div className="-m-1.5 p-1.5">
              <span className="sr-only">Mana.bio</span>
              <img alt="" src={logo} className="h-8 w-auto" />
            </div>
          </div>
          <div className="flex lg:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon aria-hidden="true" className="size-6" />
            </button>
          </div>
          <div className="hidden lg:flex lg:gap-x-12">
            {navigation.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                target={item.target || "_self"}
                className={classNames(
                  "text-sm/6 font-semibold text-gray-900 border-b-2 hover:border-gray-300",
                  isCurrent(item.href)
                    ? "border-indigo-600"
                    : "border-transparent"
                )}
                onClick={item.onClick}
              >
                {item.name}
                {item.icon && (
                  <item.icon className="inline-block ml-1 h-4 w-4" />
                )}
              </Link>
            ))}
          </div>
        </nav>
        <Dialog
          open={mobileMenuOpen}
          onClose={setMobileMenuOpen}
          className="lg:hidden"
        >
          <div className="fixed inset-0 z-50" />
          <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white p-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
            <div className="flex items-center justify-between">
              <Link href="#" className="-m-1.5 p-1.5">
                <span className="sr-only">Mana.bio</span>
                <img alt="" src={logo} className="h-8 w-auto" />
              </Link>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="-m-2.5 rounded-md p-2.5 text-gray-700"
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-gray-500/10">
                <div className="space-y-2 py-6">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      href={item.href}
                      target={item.target || "_self"}
                      className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </DialogPanel>
        </Dialog>
      </header>
    </div>
  );
}
